<template>
  <div class="new-dispatch-modal">
    <div class="form-grid">
      <div class="form-group">
        <label for="datePicker">Dispatch Date<span class="required">*</span></label>
        <input
            type="date"
            v-model="formData.date"
            id="datePicker"
            class="search-input styled-input"
            disabled
        >
      </div>

      <div class="form-group">
        <label for="salesRep">Vehicle <span class="required">*</span></label>
        <select v-model="formData.salesRep" id="salesRep" class="styled-input" :disabled="hasSelectedAllocations">
          <option disabled value="">Select Vehicle</option>
          <option v-for="rep in salesReps" :key="rep.profile_id" :value="rep.profile_id">{{ rep.name }}</option>
        </select>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="formData.salesRep && allocationsLoading" class="allocations-loading">
      <i class="fa fa-spinner fa-spin"></i>
      <span>Fetching stock allocations…</span>
    </div>

    <!-- Allocations list -->
    <div v-else-if="formData.salesRep && selectedRepStockAllocations.length" class="allocations-card">
      <div class="allocations-card__header">
        <span class="allocations-eyebrow">Stock Allocations</span>
        <span class="allocations-count">{{ selectedRepStockAllocations.length }} found</span>
      </div>

      <div class="allocations-table-wrap">
        <table class="allocations-table">
          <thead>
          <tr>
            <th class="col-select"></th>
            <th>Allocation No</th>
            <th>Date</th>
            <th>Item</th>
            <th>Qty</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="allocation in selectedRepStockAllocations"
              :key="allocation.id"
              :class="{ 'is-selected': selectedAllocations.includes(allocation.id) }"
          >
            <td class="col-select">
              <input type="checkbox" v-model="selectedAllocations" :value="allocation.id">
            </td>
            <td class="allocation-no">{{ allocation.stock_allocations_no }}</td>
            <td>{{ allocation.stock_allocations_date }}</td>
            <td>
              <ul class="item-list">
                <li v-for="item in allocation.items" :key="item.item_id">{{ item.item_name }}</li>
              </ul>
            </td>
            <td>
              <ul class="item-list">
                <li v-for="item in allocation.items" :key="item.item_id">{{ parseInt(item.item_qty) }}</li>
              </ul>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="formData.salesRep && !selectedRepStockAllocations.length" class="allocations-empty">
      <i class="fa fa-inbox"></i>
      <p>No stock allocations found</p>
      <span>This vehicle has no pending stock allocations to dispatch.</span>
    </div>

    <div class="button-group mt-5">
      <button type="submit" class="btn btn-success" @click="generateDispatch" :disabled="submitLoading">
        <i v-if="submitLoading" class="fa fa-spinner fa-spin mr-2"></i>
        {{ submitLoading ? 'Generating...' : 'Generate Dispatch' }}
      </button>
    </div>

    <!-- Conflict Modal -->
    <GatePassConflictModal
        v-if="showConflictModal"
        :message="conflictMessage"
        :dispatches="conflictData"
        @close="showConflictModal = false"
    />
  </div>
</template>

<script>
import { fetchSalesRepList, fetchREpDispatchList, saveDispatch } from '@/services/dataService';
import GatePassConflictModal from '@/pages/Modal/GatePassConflictModal.vue';
import { useRouter, useRoute } from "vue-router";
import { useToast } from 'vue-toastification';

export default {
  name: "NewDispatch",
  components: {
    GatePassConflictModal
  },
  data() {
    return {
      formData: {
        date: new Date().toISOString().split('T')[0],
        salesRep: "",
        company_id: null
      },
      salesReps: [],
      selectedRepStockAllocations: [],
      selectedAllocations: [],
      allocationsLoading: false,
      profile_id: null,
      token: localStorage.getItem('token_set') || '',
      submitLoading: false,
      showConflictModal: false,
      conflictMessage: '',
      conflictData: []
    };
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const toast = useToast();
    return { router, route, toast };
  },
  computed: {
    hasSelectedAllocations() {
      return this.selectedAllocations.length > 0;
    }
  },
  async mounted() {
    this.profile_id = localStorage.getItem('profile_id');
    this.formData.company_id = localStorage.getItem('company_id');
    await this.fetchDispatchData();

    const preselectedVehicle = this.route?.query?.vehicle;
    if (preselectedVehicle) {
      this.formData.salesRep = Array.isArray(preselectedVehicle)
          ? preselectedVehicle[0]
          : preselectedVehicle;
    }
  },
  watch: {
    'formData.salesRep': async function (newVal) {
      this.selectedRepStockAllocations = [];
      this.selectedAllocations = [];
      if (newVal) await this.fetchRepStockAllocations(newVal);
    }
  },
  methods: {
    async fetchDispatchData() {
      try {
        const reps = await fetchSalesRepList(this.profile_id, this.token);
        this.salesReps = reps.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchRepStockAllocations(profileId) {
      this.allocationsLoading = true;
      try {
        const res = await fetchREpDispatchList(profileId, this.token);
        this.selectedRepStockAllocations = res.data.data ?? [];
      } catch (e) {
        console.error(e);
        this.selectedRepStockAllocations = [];
      } finally {
        this.allocationsLoading = false;
      }
    },
    getValidationError() {
      if (!this.formData.salesRep) return "Please select a Vehicle.";
      if (this.selectedAllocations.length === 0) return "Please select at least one Stock Allocation.";
      return null;
    },
    async generateDispatch() {
      const errorMsg = this.getValidationError();
      if (errorMsg) {
        this.toast.error(errorMsg);
        return;
      }

      try {
        this.submitLoading = true;
        const payload = {
          created_by: this.profile_id,
          company_id: this.formData.company_id,
          checked_by: this.profile_id,
          rep_id: this.formData.salesRep,
          stock_allocations_id: this.selectedAllocations,
          sales_dispatches_date: this.formData.date
        };

        const response = await saveDispatch(payload, this.token);
        if (response.data.status === "success") {
          this.toast.success(response.data.message);
          this.router.push('/dispatch');
        } else if (response.data.status === "conflict") {
          this.conflictMessage = response.data.message || "Before creating a new dispatch, ensure that the gate pass is in for dispatches from more than 2 days ago.";
          this.conflictData = response.data.data || [];
          this.showConflictModal = true;
        } else {
          this.toast.error(response.data.message);
        }
      } catch (error) {
        this.toast.error("Failed to generate dispatch.");
      } finally {
        this.submitLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.new-dispatch-modal {
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

@media (max-width: 600px) { .form-grid { grid-template-columns: 1fr; } }

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
}

label {
  font-weight: bold;
  margin-bottom: 8px;
  color: #1b2a50;
}

.required { color: red; }

.styled-input {
  height: 42px;
  padding: 0 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  width: 100%;
}

.styled-input:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  border-style: dashed;
}

/* ======================== LOADING STATE ======================== */
.allocations-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  margin-top: 20px;
  color: #2563eb;
  font-size: 0.9rem;
  font-weight: 500;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.allocations-loading .fa {
  font-size: 2rem;
}

/* ======================== EMPTY STATE ======================== */
.allocations-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  margin-top: 20px;
  text-align: center;
  color: #9ca3af;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px dashed #e5e7eb;
}

.allocations-empty .fa {
  font-size: 2.2rem;
  color: #d1d5db;
  margin-bottom: 4px;
}

.allocations-empty p {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #6b7280;
}

.allocations-empty span {
  font-size: 0.8rem;
  color: #9ca3af;
}

/* ======================== ALLOCATIONS CARD ======================== */
.allocations-card {
  margin-top: 20px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.allocations-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f8faff;
  border-bottom: 1px solid #e5e7eb;
}

.allocations-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}

.allocations-count {
  font-size: 0.72rem;
  font-weight: 600;
  color: #2563eb;
  background: #dbeafe;
  border-radius: 20px;
  padding: 3px 10px;
}

.allocations-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 320px;
}

.allocations-table {
  width: 100%;
  border-collapse: collapse;
}

.allocations-table th {
  padding: 10px 14px;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  /* Sticky header */
  position: sticky;
  top: 0;
  background: #f8faff;
  z-index: 1;
}

.allocations-table td {
  padding: 12px 14px;
  font-size: 0.85rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.allocations-table tbody tr:last-child td {
  border-bottom: none;
}

.allocations-table tbody tr:hover {
  background: #f9fafb;
}

.allocations-table tbody tr.is-selected {
  background: #eff6ff;
}

.allocations-table .col-select {
  width: 40px;
}

.allocation-no {
  font-weight: 600;
  color: #1f2937;
}

.item-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.table {
  width: 100%;
  margin-top: 20px;
  border-collapse: collapse;
}

.table th, .table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.btn-success {
  background-color: #28a745;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.button-group {
  display: flex;
  justify-content: flex-end;
}
</style>